import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import SignaturePad from './SignaturePad';

const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";
const labelCls = "text-xs text-muted-foreground block mb-1";

const REASONS = [

  'Agency refused to release information',
  'Unable to locate Agency',
  'Other',
];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function GoodFaithModal({ driver, onClose }) {
  const [form, setForm] = useState({
    applicant_name: driver.full_name || '',
    ssn: driver.ein || '',
    dob: '',
    license_number: driver.cdl_number || '',
    license_state: '',
    agency_name: '',
    agency_phone: '',
    agency_fax: '',
    agency_address: '',
    agency_city: '',
    agency_state: '',
    agency_zip: '',
    contact_date: '',
    reason: '',
    other_explain: '',
    conducted_by: '',
    signature_data: '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.contact_date || !form.reason || !form.signature_data) {
      toast.error('Please fill in all required fields and provide a signature.');
      return;
    }
    setSaving(true);
    await client.entities.DriverProfile.update(driver.id, {
      good_faith_data: JSON.stringify(form),
    });
    toast.success('Good Faith form saved.');
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl z-10 max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-border flex-shrink-0">
          <div>
            <h2 className="font-space font-bold text-foreground text-lg">Good Faith Effort Driving Record Inquiry</h2>
            <p className="text-xs text-muted-foreground mt-0.5">In accordance with 49 CFR Part 391.23 the following record is submitted:</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto p-6 space-y-6 flex-1">

          {/* Driver Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className={labelCls}>Applicant's Name <span className="text-red-400">*</span></label>
              <input value={form.applicant_name} onChange={e => set('applicant_name', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>SSN <span className="text-red-400">*</span></label>
              <input value={form.ssn} onChange={e => set('ssn', e.target.value)} placeholder="xxx-xx-0000" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>DOB <span className="text-red-400">*</span></label>
              <input type="date" value={form.dob} onChange={e => set('dob', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>License Number <span className="text-red-400">*</span></label>
              <input value={form.license_number} onChange={e => set('license_number', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>State <span className="text-red-400">*</span></label>
              <select value={form.license_state} onChange={e => set('license_state', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50">
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </div>
          </div>

          {/* State Agency Contacted */}
          <div>
            <h3 className="font-space font-semibold text-foreground text-sm mb-3 pb-1 border-b border-border">State Agency Contacted</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Name <span className="text-red-400">*</span></label>
                <input value={form.agency_name} onChange={e => set('agency_name', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Phone <span className="text-red-400">*</span></label>
                <input value={form.agency_phone} onChange={e => set('agency_phone', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Fax</label>
                <input value={form.agency_fax} onChange={e => set('agency_fax', e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-3">
                <label className={labelCls}>Address <span className="text-red-400">*</span></label>
                <input value={form.agency_address} onChange={e => set('agency_address', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>City <span className="text-red-400">*</span></label>
                <input value={form.agency_city} onChange={e => set('agency_city', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>State <span className="text-red-400">*</span></label>
                <select value={form.agency_state} onChange={e => set('agency_state', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50">
                  <option value="" className="bg-background">State</option>
                  {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Zipcode <span className="text-red-400">*</span></label>
                <input value={form.agency_zip} onChange={e => set('agency_zip', e.target.value)} className={inputCls} />
              </div>
            </div>
          </div>

          {/* Good Faith Questionnaire */}
          <div>
            <h3 className="font-space font-semibold text-foreground text-sm mb-3 pb-1 border-b border-border">Good Faith Questionnaire</h3>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Date Attempted Contact Was Made <span className="text-red-400">*</span></label>
                <input type="date" value={form.contact_date} onChange={e => set('contact_date', e.target.value)} className={inputCls} />
              </div>

              <div>
                <label className={labelCls + " mb-2"}>Reason For Good Faith Document <span className="text-red-400">*</span></label>
                <div className="space-y-2">
                  {REASONS.map(r => (
                    <label key={r} className="flex items-center gap-2.5 cursor-pointer group">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${form.reason === r ? 'border-primary bg-primary' : 'border-border group-hover:border-border'}`}>
                        {form.reason === r && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
                      </div>
                      <input type="radio" className="hidden" checked={form.reason === r} onChange={() => set('reason', r)} />
                      <span className="text-sm text-foreground">{r}</span>
                    </label>
                  ))}
                </div>
              </div>

              {form.reason === 'Other' && (
                <div>
                  <label className={labelCls}>Other, Explain:</label>
                  <textarea
                    value={form.other_explain}
                    onChange={e => set('other_explain', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
                  />
                </div>
              )}

              {/* Signature */}
              <div>
                <label className={labelCls}>
                  Conducted By Signature <span className="text-red-400">*</span>
                </label>
                <SignaturePad onChange={val => set('signature_data', val)} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-3 flex-shrink-0">
          <Button variant="outline" className="border-border" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Good Faith Form</>}
          </Button>
        </div>
      </div>
    </div>
  );
}